import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  CheckCheck,
  Loader2,
  Inbox,
  RefreshCw,
  MoreVertical,
  Trash2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { useNotificacoesStore } from "@/stores/notificacoesStore";
import type { Notificacao } from "@/components/notificacoes/core/models";
import { RemoteDataSource, Settings } from "@/datasources/remote_datasource";

const api = new RemoteDataSource(Settings.urlBase);

const categoriaLabel = (categoria?: string) => {
  if (!categoria) return "";
  return categoria;
};

const categoriaColor = (categoria?: string) => {
  switch (categoria) {
    case "Alterações da ocorrência":
      return "bg-primary/10 text-primary";
    case "Atualização do app":
      return "bg-blue-500/10 text-blue-600 dark:text-blue-400";
    case "Novidades":
      return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400";
    case "Promoções/Anúncios":
      return "bg-amber-500/10 text-amber-600 dark:text-amber-400";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const extractList = (raw: unknown): Notificacao[] => {
  if (Array.isArray(raw)) return raw as Notificacao[];
  if (raw && typeof raw === "object") {
    const r = raw as Record<string, unknown>;
    if (Array.isArray(r.data)) return r.data as Notificacao[];
    if (Array.isArray(r.items)) return r.items as Notificacao[];
    if (Array.isArray(r.notificacoes)) return r.notificacoes as Notificacao[];
  }
  return [];
};

export function NotificacoesPopover() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [marcandoTodas, setMarcandoTodas] = useState(false);
  const observerTarget = useRef<HTMLDivElement | null>(null);

  const {
    notificacoes,
    isLoading,
    isLoadingMore,
    hasMore,
    pageNumber,
    fetchNotificacoes,
    resetPagination,
    marcarComoLida,
    removerNotificacao,
  } = useNotificacoesStore();

  const loadMore = useCallback(() => {
    if (hasMore && !isLoadingMore && !isLoading) {
      fetchNotificacoes(false, pageNumber + 1, false);
    }
  }, [hasMore, isLoadingMore, isLoading, pageNumber, fetchNotificacoes]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && !isLoading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );
    const currentTarget = observerTarget.current;
    if (currentTarget) observer.observe(currentTarget);
    return () => { if (currentTarget) observer.unobserve(currentTarget); };
  }, [loadMore, hasMore, isLoadingMore, isLoading]);

  // Busca inicial
  useEffect(() => {
    fetchNotificacoes(false, 1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset e busca quando abre
  useEffect(() => {
    if (open) {
      resetPagination();
      fetchNotificacoes(false, 1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const naoLidas = useMemo(
    () => notificacoes.filter((n) => !n.lida).length,
    [notificacoes]
  );

  const marcarTodasComoLidas = async () => {
    const pendentes = notificacoes.filter((n) => !n.lida);
    if (pendentes.length === 0) return;
    setMarcandoTodas(true);
    try {
      await Promise.all(
        pendentes.map((n) => marcarComoLida(n.id))
      );
    } finally {
      setMarcandoTodas(false);
    }
  };

  const handleExcluirNotificacao = async (id: string) => {
    try {
      await removerNotificacao(id);
      toast({
        title: "Notificação excluída",
        description: "A notificação foi removida com sucesso.",
      });
    } catch (error) {
      toast({
        title: "Erro ao excluir notificação",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const formatarData = (data: string) => {
    try {
      return formatDistanceToNow(new Date(data), {
        addSuffix: true,
        locale: ptBR,
      });
    } catch {
      return data;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label="Notificações"
        >
          <Bell className="h-5 w-5" />
          {naoLidas > 0 && (
            <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold flex items-center justify-center">
              {naoLidas > 99 ? "99+" : naoLidas}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[380px] p-0 overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <h3 className="font-heading text-sm font-semibold">Notificações</h3>
            <p className="text-caption">
              {naoLidas > 0
                ? `${naoLidas} não ${naoLidas === 1 ? "lida" : "lidas"}`
                : "Tudo em dia"}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                resetPagination();
                fetchNotificacoes(false, 1, true);
              }}
              disabled={isLoading}
              aria-label="Atualizar"
            >
              <RefreshCw
                className={cn("h-4 w-4", isLoading && "animate-spin")}
              />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs gap-1"
              onClick={marcarTodasComoLidas}
              disabled={marcandoTodas || naoLidas === 0}
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Ler todas
            </Button>
          </div>
        </div>
        <Separator />

        {/* Lista */}
        <ScrollArea className="h-[420px]">
          {isLoading && notificacoes.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : notificacoes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
                <Inbox className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium">Sem notificações</p>
              <p className="text-caption mt-1">
                Você será avisado quando algo novo acontecer.
              </p>
            </div>
          ) : (
            <>
              <ul className="divide-y divide-border">
                {notificacoes.map((n) => (
                <li
                  key={n.id}
                  className={cn(
                    "px-4 py-3 hover:bg-muted/40 cursor-pointer transition-colors relative",
                    !n.lida && "bg-primary/[0.04]"
                  )}
                  onClick={() => {
                    if (!n.lida) marcarComoLida(n.id);
                    setOpen(false);
                    navigate(`/notificacoes/${n.id}`);
                  }}
                >
                  <div className="flex items-start gap-3">
                    {!n.lida && (
                      <span className="absolute left-1.5 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-primary" />
                    )}
                    <div className="flex-1 min-w-0 ml-2">
                      <div className="flex items-start justify-between gap-2">
                        <p
                          className={cn(
                            "text-sm leading-snug",
                            !n.lida ? "font-semibold" : "font-medium"
                          )}
                        >
                          {n.titulo}
                        </p>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className="text-[10px] text-muted-foreground whitespace-nowrap mt-0.5">
                            {formatarData(n.dataEnvio)}
                          </span>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                onClick={(e) => e.stopPropagation()}
                                className="h-6 w-6 rounded-md flex items-center justify-center hover:bg-muted transition-colors text-muted-foreground"
                                aria-label="Mais opções"
                              >
                                <MoreVertical className="h-3.5 w-3.5" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onSelect={(e) => {
                                  e.preventDefault();
                                  handleExcluirNotificacao(n.id);
                                }}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Excluir notificação
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {n.corpo}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span
                          className={cn(
                            "text-[10px] px-2 py-0.5 rounded-full font-medium",
                            categoriaColor(n.categoria)
                          )}
                        >
                          {categoriaLabel(n.categoria)}
                        </span>
                        {n.solicitacaoId && (
                          <span className="text-[10px] text-muted-foreground">
                            #{n.solicitacaoId}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            {isLoadingMore && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            <div ref={observerTarget} className="h-1" />
            </>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
